import React, { useEffect, useState, Fragment } from "react";
import { Menu, Transition, Dialog } from "@headlessui/react";
import { FiLogOut, FiBell, FiLock, FiUser, FiSettings } from "react-icons/fi";
import { MdOutlineNotificationsActive } from "react-icons/md";
import { HiOutlineKey } from "react-icons/hi";
import { RiNotificationLine } from "react-icons/ri";
import { IoCloseOutline } from "react-icons/io5";
import toast from "react-hot-toast";
import axios from "axios";
import { requestForToken, onMessageListener } from "../../firebase";
import { motion, AnimatePresence } from "framer-motion";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

export default function Navbar() {
  const [role, setRole] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);

  // -------------------------------------------------------
  // Load Role
  // -------------------------------------------------------
  useEffect(() => {
    const storedRole = localStorage.getItem("role");
    if (storedRole) setRole(storedRole);
  }, []);

  // -------------------------------------------------------
  // ⭐ INIT FCM + Subscribe Admin to Topic
  // -------------------------------------------------------
  useEffect(() => {
    const initFCM = async () => {
      console.log("⚡ Requesting FCM Token...");

      const token = await requestForToken();
      if (!token) return;

      console.log("🔥 Admin FCM Token:", token);
      localStorage.setItem("fcm_token", token);

      // Subscribe to topic
      try {
        await axios.post(`${BASE_URL}/v1/api/subscribe-admin`, { token });
        console.log("📌 Admin subscribed to: allAdmins");
      } catch (err) {
        console.error("❌ Topic subscription failed", err);
      }
    };

    initFCM();
  }, []);

  // -------------------------------------------------------
  // ⭐ Foreground Notification Listener
  // -------------------------------------------------------
  useEffect(() => {
    onMessageListener()
      .then((payload) => {
        console.log("📩 Foreground Notification:", payload);

        const title = payload.notification?.title || "Notification";
        const body = payload.notification?.body || "";

        // Custom toast notification
        toast.custom((t) => (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            className={`${
              t.visible ? "animate-enter" : "animate-leave"
            } max-w-md w-full bg-gradient-to-r from-cyan-900 to-blue-900 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
          >
            <div className="flex-1 w-0 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <RiNotificationLine className="h-6 w-6 text-cyan-400" />
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-white">{title}</p>
                  <p className="mt-1 text-sm text-gray-300">{body}</p>
                </div>
              </div>
            </div>
            <div className="flex border-l border-gray-700">
              <button
                onClick={() => toast.dismiss(t.id)}
                className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-gray-300 hover:text-white focus:outline-none"
              >
                <IoCloseOutline className="h-5 w-5" />
              </button>
            </div>
          </motion.div>
        ));

        const newNotification = {
          title,
          body,
          time: new Date().toLocaleString(),
          id: Date.now(),
          read: false,
        };

        setNotifications((prev) => {
          const updated = [newNotification, ...prev];
          return updated.slice(0, 20);
        });

        setUnread((prev) => prev + 1);
      })
      .catch((err) => console.error("FCM listener error:", err));
  }, []);

  // -------------------------------------------------------
  // Password Reset Handler
  // -------------------------------------------------------
  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters!");
      return;
    }

    try {
      const adminId = localStorage.getItem("userId");
      if (!adminId) return toast.error("User not found!");

      const response = await axios.post(
        `${BASE_URL}/v1/api/admin/resetPassword`,
        { admin_id: adminId, new_password: newPassword },
      );

      if (response.status === 200) {
        toast.success("Password updated successfully!");
        setIsOpen(false);
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (err) {
      toast.error("Failed to update password.");
    }
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const markAsRead = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    setUnread((prev) => Math.max(0, prev - 1));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    setUnread(0);
  };

  return (
    <>
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="bg-gradient-to-r from-gray-900 via-gray-900 to-gray-900 h-16 px-6 flex items-center justify-between w-full border-b border-gray-800 shadow-xl"
      >
        {/* Left side - Empty or can add page title later */}
        <div className="flex items-center">
          {/* You can add a page title or breadcrumb here if needed */}
        </div>

        {/* Right side - User info and actions */}
        <div className="flex items-center gap-4">
          {/* Welcome Text */}
          <div className="hidden lg:block text-right">
            <p className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 text-sm font-semibold">
              Welcome back,
            </p>
            <p className="text-gray-300 text-xs">{role || "Administrator"}</p>
          </div>

          {/* Notification Bell */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-lg hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-blue-500/10 transition-all duration-200 group"
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="relative"
              >
                <FiBell className="text-cyan-400 text-xl group-hover:text-cyan-300 transition-colors" />
                {unread > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center shadow-lg shadow-red-500/30"
                  >
                    {unread > 9 ? "9+" : unread}
                  </motion.span>
                )}
              </motion.div>
            </button>

            {/* Notification Dropdown */}
            <AnimatePresence>
              {showNotifications && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-2 w-96 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl shadow-2xl border border-gray-700 overflow-hidden z-50"
                >
                  {/* Header */}
                  <div className="p-4 border-b border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MdOutlineNotificationsActive className="text-cyan-400 text-xl" />
                        <h3 className="text-lg font-semibold text-white">
                          Notifications
                        </h3>
                        {unread > 0 && (
                          <span className="bg-cyan-500 text-white text-xs px-2 py-0.5 rounded-full">
                            {unread} new
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {unread > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors px-2 py-1 hover:bg-cyan-500/10 rounded"
                          >
                            Mark all read
                          </button>
                        )}
                        <button
                          onClick={clearAllNotifications}
                          className="text-xs text-gray-400 hover:text-red-400 transition-colors"
                        >
                          Clear all
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Notification List */}
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center">
                        <RiNotificationLine className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400">No notifications yet</p>
                        <p className="text-sm text-gray-500 mt-1">
                          New notifications will appear here
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-700/50">
                        {notifications.map((notification) => (
                          <motion.div
                            key={notification.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className={`p-4 hover:bg-gradient-to-r hover:from-cyan-500/5 hover:to-blue-500/5 transition-all duration-200 cursor-pointer ${
                              !notification.read
                                ? "bg-gradient-to-r from-cyan-500/10 to-blue-500/10"
                                : ""
                            }`}
                            onClick={() => markAsRead(notification.id)}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`mt-1 ${!notification.read ? "text-cyan-400" : "text-gray-500"}`}
                              >
                                <RiNotificationLine className="h-5 w-5" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start justify-between">
                                  <p
                                    className={`font-medium ${!notification.read ? "text-white" : "text-gray-300"}`}
                                  >
                                    {notification.title}
                                  </p>
                                  <span className="text-xs text-gray-500">
                                    {notification.time}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-400 mt-1">
                                  {notification.body}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Profile Menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center hover:ring-2 hover:ring-cyan-500/50 p-1 rounded-xl transition-all duration-200 group">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative"
              >
                <div
                  className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 border-2 border-cyan-300/50 bg-cover bg-center shadow-lg"
                  style={{
                    backgroundImage:
                      'url("https://i.pinimg.com/474x/67/c3/d6/67c3d63e215e034e01d45c8256d720d3.jpg")',
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-xl"></div>
                </div>
                <div className="absolute -bottom-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-gray-900 shadow"></div>
              </motion.div>
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-150"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-64 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl shadow-2xl border border-gray-700 overflow-hidden z-50">
                {/* Profile Info */}
                <div className="p-4 border-b border-gray-700">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-12 w-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 border-2 border-cyan-300/50 bg-cover bg-center"
                      style={{
                        backgroundImage:
                          'url("https://i.pinimg.com/474x/67/c3/d6/67c3d63e215e034e01d45c8256d720d3.jpg")',
                      }}
                    ></div>
                    <div>
                      <p className="font-semibold text-white">{role}</p>
                      <p className="text-sm text-cyan-400">Administrator</p>
                      <p className="text-xs text-gray-400">
                        Super Admin Access
                      </p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  {/* <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => {}}
                        className={`${
                          active ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10' : ''
                        } w-full px-4 py-3 flex items-center gap-3 text-gray-300 hover:text-white transition-colors`}
                      >
                        <FiUser className="text-cyan-400" />
                        <span>Profile Settings</span>
                      </button>
                    )}
                  </Menu.Item> */}

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => setIsOpen(true)}
                        className={`${
                          active
                            ? "bg-gradient-to-r from-cyan-500/10 to-blue-500/10"
                            : ""
                        } w-full px-4 py-3 flex items-center gap-3 text-gray-300 hover:text-white transition-colors`}
                      >
                        <HiOutlineKey className="text-cyan-400" />
                        <span>Change Password</span>
                      </button>
                    )}
                  </Menu.Item>

                  {/* <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => {}}
                        className={`${
                          active ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10' : ''
                        } w-full px-4 py-3 flex items-center gap-3 text-gray-300 hover:text-white transition-colors`}
                      >
                        <FiSettings className="text-cyan-400" />
                        <span>Preferences</span>
                      </button>
                    )}
                  </Menu.Item> */}
                </div>

                {/* Logout */}
                <div className="border-t border-gray-700">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => {
                          localStorage.clear();
                          window.location.href = "/";
                        }}
                        className={`${
                          active
                            ? "bg-gradient-to-r from-red-500/10 to-pink-500/10"
                            : ""
                        } w-full px-4 py-3 flex items-center gap-3 text-red-400 hover:text-red-300 transition-colors`}
                      >
                        <FiLogOut />
                        <span>Logout</span>
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </motion.div>

      {/* PASSWORD MODAL */}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog className="relative z-50" onClose={() => setIsOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl w-full max-w-md shadow-2xl border border-gray-700 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <Dialog.Title className="text-xl font-bold text-white">
                        Change Password
                      </Dialog.Title>
                      <p className="text-sm text-gray-400 mt-1">
                        Update your account password
                      </p>
                    </div>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <IoCloseOutline className="h-5 w-5 text-gray-400" />
                    </button>
                  </div>

                  <form onSubmit={handlePasswordReset} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-500"
                          placeholder="Enter new password"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-500"
                        placeholder="Confirm new password"
                      />
                    </div>

                    <div className="pt-4">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => setIsOpen(false)}
                          className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                        >
                          Cancel
                        </button>

                        <button
                          type="submit"
                          className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-xl transition-all duration-200 shadow-lg shadow-cyan-500/25"
                        >
                          Update Password
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
